import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllJobPosts, updateJobPostStatus } from '../services/api';

const JobPostManagement = () => {
  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    const loadJobPosts = async () => {
      const result = await getAllJobPosts();
      if (result.success) {
        setJobPosts(result.jobPosts);
      }
      setLoading(false);
    };
    loadJobPosts();
  }, []);

  const handleApproveJob = async (jobPostId) => {
    const result = await updateJobPostStatus(jobPostId, 'approved');
    if (result.success) {
      setJobPosts(jobPosts.map(jp =>
        jp.id === jobPostId ? { ...jp, status: 'approved' } : jp
      ));
    }
  };

  const handleRejectJob = async (jobPostId) => {
    const result = await updateJobPostStatus(jobPostId, 'rejected');
    if (result.success) {
      setJobPosts(jobPosts.map(jp =>
        jp.id === jobPostId ? { ...jp, status: 'rejected' } : jp
      ));
    }
  };

  const viewJobDetails = (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedJob(null);
  };

  const pendingJobs = jobPosts.filter(jp => jp.status === 'pending');
  const approvedJobs = jobPosts.filter(jp => jp.status === 'approved');
  const rejectedJobs = jobPosts.filter(jp => jp.status === 'rejected');

  const getFilteredJobs = () => {
    switch (activeTab) {
      case 'pending':
        return pendingJobs;
      case 'approved':
        return approvedJobs;
      case 'rejected':
        return rejectedJobs;
      default:
        return jobPosts;
    }
  };

  const filteredJobs = getFilteredJobs();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-3">
                  <span className="text-5xl mr-4">📝</span>
                  <h1 className="text-4xl font-bold">
                    Job Post Management
                  </h1>
                </div>
                <p className="text-indigo-100 text-lg">
                  Manage all job postings across the platform
                </p>
              </div>
              <Link
                to="/admin/dashboard"
                className="bg-white text-indigo-600 px-6 py-3 rounded-xl hover:bg-indigo-50 font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-200 text-sm font-medium mb-1">Total Jobs</p>
                <p className="text-4xl font-bold">{jobPosts.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">📋</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium mb-1">Pending</p>
                <p className="text-4xl font-bold">{pendingJobs.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">⏳</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">Approved</p>
                <p className="text-4xl font-bold">{approvedJobs.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">✓</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium mb-1">Rejected</p>
                <p className="text-4xl font-bold">{rejectedJobs.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">✗</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-8 border border-gray-100">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Jobs ({jobPosts.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'pending'
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ⏳ Pending ({pendingJobs.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'approved'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ✓ Approved ({approvedJobs.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'rejected'
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ✗ Rejected ({rejectedJobs.length})
            </button>
          </div>
        </div>

        {/* Job Posts List */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {loading ? (
            <div className="text-center py-16">
              <div className="relative inline-block">
                <div className="text-6xl mb-4 animate-bounce">⏳</div>
              </div>
              <p className="text-gray-500 text-xl font-medium">Loading job posts...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-dashed border-gray-300">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-500 text-xl font-medium">No {activeTab !== 'all' ? activeTab : ''} job posts found</p>
            </div>
          ) : (
            <div className="space-y-5">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className={`border-2 rounded-2xl p-6 transition-all hover:shadow-2xl transform hover:scale-[1.01] ${
                    job.status === 'pending'
                      ? 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50'
                      : job.status === 'approved'
                      ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50'
                      : 'border-red-300 bg-gradient-to-br from-red-50 to-pink-50'
                  }`}
                >
                  {/* Job Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-800">{job.position}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            job.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : job.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {job.status === 'approved'
                            ? '✓ Approved'
                            : job.status === 'rejected'
                            ? '✗ Rejected'
                            : '⏳ Pending'}
                        </span>
                      </div>
                      <p className="text-gray-600">
                        {job.customCompanyName || job.companyName} • Posted by {job.employerName}
                      </p>
                    </div>
                  </div>

                  {/* Job Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="flex items-center text-gray-700">
                      <span className="mr-2">📍</span>
                      <span className="text-sm">{job.location}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <span className="mr-2">💰</span>
                      <span className="text-sm">{job.salary}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <span className="mr-2">👥</span>
                      <span className="text-sm">{job.vacancies} vacancies</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <span className="mr-2">🕒</span>
                      <span className="text-sm">{job.type}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <span className="mr-2">📅</span>
                      <span className="text-sm">{job.postedDate}</span>
                    </div>
                  </div>

                  {/* Required Skills */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2 font-semibold">Required Skills:</p>
                    <div className="flex flex-wrap gap-2">
                      {job.requiredSkills && job.requiredSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => viewJobDetails(job)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-semibold transition-all"
                    >
                      👁️ View Full Details
                    </button>
                    {job.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApproveJob(job.id)}
                          className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-semibold transition-all"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => handleRejectJob(job.id)}
                          className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-semibold transition-all"
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    {job.status === 'approved' && (
                      <button
                        onClick={() => handleRejectJob(job.id)}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-semibold transition-all"
                      >
                        Revoke Approval
                      </button>
                    )}
                    {job.status === 'rejected' && (
                      <button
                        onClick={() => handleApproveJob(job.id)}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-semibold transition-all"
                      >
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Job Details Modal */}
        {showDetailsModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedJob.position}</h2>
                    <p className="text-blue-100 mt-1">
                      {selectedJob.customCompanyName || selectedJob.companyName}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
                  >
                    <span className="text-2xl">✕</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedJob.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : selectedJob.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {selectedJob.status === 'approved'
                      ? '✓ Approved'
                      : selectedJob.status === 'rejected'
                      ? '✗ Rejected'
                      : '⏳ Pending'}
                  </span>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border-l-4 border-blue-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <span className="text-xl mr-2">💼</span>
                    Job Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Location</p>
                      <p className="font-semibold text-gray-800">📍 {selectedJob.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Salary</p>
                      <p className="font-semibold text-gray-800">💰 {selectedJob.salary}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Vacancies</p>
                      <p className="font-semibold text-gray-800">👥 {selectedJob.vacancies}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Type</p>
                      <p className="font-semibold text-gray-800">🕒 {selectedJob.type}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-1">Posted By</p>
                    <p className="font-semibold text-gray-800">👤 {selectedJob.employerName}</p>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">Posted Date</p>
                    <p className="font-semibold text-gray-800">📅 {selectedJob.postedDate}</p>
                  </div>
                </div>

                {/* Required Skills */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border-l-4 border-purple-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">🎯</span>
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.requiredSkills &&
                      selectedJob.requiredSkills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold"
                        >
                          {skill}
                        </span>
                      ))}
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border-l-4 border-green-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">📋</span>
                    Job Description
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedJob.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 border-l-4 border-blue-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">✓</span>
                      Responsibilities
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-blue-500 mr-2 mt-1">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-5 border-l-4 border-yellow-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">📌</span>
                      Requirements
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-yellow-600 mr-2 mt-1">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border-l-4 border-indigo-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">🎁</span>
                      Benefits
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-indigo-500 mr-2 mt-1">•</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  {selectedJob.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          handleApproveJob(selectedJob.id);
                          closeModal();
                        }}
                        className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition-all"
                      >
                        ✓ Approve Job
                      </button>
                      <button
                        onClick={() => {
                          handleRejectJob(selectedJob.id);
                          closeModal();
                        }}
                        className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all"
                      >
                        ✗ Reject Job
                      </button>
                    </>
                  )}
                  {selectedJob.status === 'approved' && (
                    <button
                      onClick={() => {
                        handleRejectJob(selectedJob.id);
                        closeModal();
                      }}
                      className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all"
                    >
                      Revoke Approval
                    </button>
                  )}
                  {selectedJob.status === 'rejected' && (
                    <button
                      onClick={() => {
                        handleApproveJob(selectedJob.id);
                        closeModal();
                      }}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition-all"
                    >
                      Approve Job
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    className="flex-1 bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-gray-500 font-semibold transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobPostManagement;
